import {
  ActionType,
  Selectors
} from 'Reducers'
import { saveSourceMetadata } from 'Services'
import { select, take, call } from 'typed-redux-saga'
import { race } from '@redux-saga/core/effects'
import { LOCATION_CHANGE } from 'connected-react-router'
import { FirebaseError } from '@firebase/util'

export function * waitForStartup() {
  const appStarted: ReturnType<typeof Selectors.isAppStarted> = yield select(Selectors.isAppStarted)

  if (!appStarted) {
    yield take(ActionType.START_UP_SUCCESS)
  }
}

export function * waitForLogin() {
  const isLoggedIn: ReturnType<typeof Selectors.isLoggedIn> = yield select(Selectors.isLoggedIn)
  if (!isLoggedIn) {
    yield take(ActionType.LOGIN_SUCCESS)
  }
}

export function * waitForFirebaseInitialization() {
  const isFirebaseInitialized: ReturnType<typeof Selectors.isFirebaseInitialized> = yield select(Selectors.isFirebaseInitialized)
  if (!isFirebaseInitialized) {
    yield take(ActionType.FIREBASE_INITIALIZED)
  }
}

export type AuthOutcome =
  | { status: 'success' }
  | { status: 'failed', error?: FirebaseError }
  /** The member left the flow, or the request was superseded — say nothing, do nothing. */
  | { status: 'abandoned' }

/**
 * Waits for the outcome of a sign-in/registration this saga asked for itself.
 *
 * The SUCCESS/FAILED actions are app-wide, so a flow that plainly `take`s them
 * wakes on *anyone's* sign-in — including one started from a different screen
 * minutes later, which then gets redeemed and navigated as if it were this
 * flow's. Worse, the request saga is `takeLatest`: a superseded worker is
 * cancelled and emits neither outcome, so an unscoped wait hangs for the rest of
 * the session and fires at the next unrelated sign-in.
 *
 * Both are ruled out here: only the outcome carrying `email` counts, and any
 * navigation out of `routePrefix` (including that superseded case, where the
 * member has moved on) ends the wait as `abandoned`.
 */
export function * waitForOwnAuthOutcome (
  { successType, failedType, email, routePrefix }: {
    successType: ActionType
    failedType: ActionType
    email: string
    routePrefix: string
  }
) {
  const isOurs = (action: { type: string, email?: string }, type: ActionType) =>
    action.type === type && action.email === email

  const leftTheFlow = (action: { type: string, payload?: { location?: { pathname?: string } } }) =>
    action.type === LOCATION_CHANGE && !(action.payload?.location?.pathname ?? '').startsWith(routePrefix)

  const outcome = (yield race({
    success: take((action: any) => isOurs(action, successType)),
    failed: take((action: any) => isOurs(action, failedType)),
    abandoned: take(leftTheFlow as any)
  })) as { success?: unknown, failed?: { error?: FirebaseError }, abandoned?: unknown }

  if (outcome.success) {
    return { status: 'success' } as AuthOutcome
  }
  if (outcome.failed) {
    return { status: 'failed', error: outcome.failed.error } as AuthOutcome
  }
  return { status: 'abandoned' } as AuthOutcome
}

export function * saveSourceMetadataSaga (purchaseId: string, purchaseType: PurchaseType, checkoutSessionId?: string) {
  try {
    yield call(saveSourceMetadata, purchaseId, purchaseType, checkoutSessionId)
  } catch (error) {
    yield call(logError, 'web_saveSourceMetadataError', error)
  }
}
